import { composeClassNames, withDefault } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties } from "react";
import { Col, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import Heading from "src/components/Heading";
import InstructorRates from "src/components/InstructorRates";
import IntroductionActions from "src/components/IntroductionActions";
import IntroductionInfo from "src/components/IntroductionInfo";
import MqGteDesktop from "src/components/MqGteDesktop";
import MqLteTablet from "src/components/MqLteTablet";
import Paragraph from "src/components/Paragraph";
import ProfileButtons from "src/components/ProfileButtons";
import ReadMore from "src/components/ReadMore";
import VideoPlayer from "src/components/VideoPlayer";
import constants from "src/constants";
import countryNameText from "src/functions/countryNameText";
import getYogaStyles from "src/functions/getYogaStyles";
import profileName from "src/functions/profileName";
import profileNameText from "src/functions/profileNameText";
import { sInstructorIntroduction } from "src/styles/sInstructorIntroduction.module.scss";
import { sIntroduction } from "src/styles/sIntroduction.module.scss";
import { ApiProfile } from "src/types/ApiProfile";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiProfile;
};

function InstructorIntroduction({ style, className, data }: Props) {
  const { locale: lang } = useRouter();
  var videoDefined = data.video
    ? Object.keys(data.video).length > 0
      ? true
      : false
    : false;
  return data ? (
    <Row
      style={style}
      className={composeClassNames([
        sIntroduction,
        sInstructorIntroduction,
        className,
        "instructor-introduction",
      ])}
    >
      <Col lg={videoDefined ? 6 : 8} className={"instructor-detail"}>
        {data.client ? (
          <Heading
            type="h1"
            title={profileName(
              data.client?.name,
              data.client?.surname,
              data.nickname,
              data.type,
              `xl`
            )}
          />
        ) : null}
        <IntroductionInfo
          type={`instructor`}
          data={[
            {
              item: data.loyalty
                ? withDefault(data.loyalty.type, `neutral`)
                : `neutral`,
            },
            {
              item: countryNameText(
                data?.locale,
                lang,
                data?.address?.country,
                data?.address?.city
              ),
            },
            { item: data.followers ? data.followers : 0 },
          ]}
        />
        {data.practice_basic ? (
          <IntroductionInfo
            type={`links`}
            data={getYogaStyles(data.practice_basic.style, lang)}
          />
        ) : null}
        <IntroductionActions data={{ favorite: true, rating: data.rating }} />
        {data.description && data.description.length > 0 ? (
          <ReadMore
            rowsVisible={constants.Text.descriptionRowsLong}
            rowsCount={
              data.description.length / constants.Text.descriptionCharsPerRow
            }
          >
            <Paragraph size="xn">
              <ReactMarkdown>{data.description}</ReactMarkdown>
            </Paragraph>
          </ReadMore>
        ) : null}
        <InstructorRates data={data.rate} />
        {data.sites ? (
          <MqLteTablet>
            <ProfileButtons
              data={{
                sites: data.sites,
                address: data.address,
                contact: {
                  nickname: profileNameText(
                    data.client?.name,
                    data.client?.surname,
                    data.nickname,
                    data.type
                  ),
                  profileType: data.type,
                  contacts: data.contact,
                },
              }}
              className={`instructor-buttons`}
              style={{
                marginTop: constants.Size.lg,
                marginBottom: constants.Size.lg,
              }}
            />
          </MqLteTablet>
        ) : null}
      </Col>
      <Col lg={videoDefined ? 6 : 4} className={"instructor-preview"}>
        <VideoPlayer data={data.video} thumbnail={data.thumbnail} />
        {data.sites ? (
          <MqGteDesktop>
            <ProfileButtons
              data={{
                sites: data.sites,
                address: data.address,
                contact: {
                  nickname: profileNameText(
                    data.client?.name,
                    data.client?.surname,
                    data.nickname,
                    data.type
                  ),
                  profileType: data.type,
                  contacts: data.contact,
                },
              }}
              className={`instructor-buttons`}
            />
          </MqGteDesktop>
        ) : null}
      </Col>
    </Row>
  ) : null;
}

export default InstructorIntroduction;
